import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  PaperPlaneTilt,
  Eye,
  Sparkle,
  CheckCircle,
  Warning,
  Envelope,
  ShieldCheck,
  PencilSimpleLine,
  Rows,
} from "@phosphor-icons/react";

const AUDIENCE_OPTIONS = [
  { key: "self_test",    label: "Just me (test send)",   tone: "muted" },
  { key: "all_owners",   label: "All owners (Pro + Free)", tone: "primary" },
  { key: "pro_owners",   label: "Pro owners only",       tone: "primary" },
  { key: "free_owners",  label: "Free owners only",      tone: "muted" },
  { key: "customers",    label: "All customers",         tone: "primary" },
];

/**
 * AdminBroadcastCard — feature-announcement + ad-hoc broadcast sender.
 *
 * Two modes (toggle at the top):
 *   1. Templates — pick one of the pre-registered Python templates
 *      (whos_looking_v1, storm_cancel_events_v1, ...). Reusable,
 *      adapts to Pro/Free, per-recipient deduped.
 *   2. Custom message — admin composes subject + markdown body in the
 *      browser, live-previews via /adhoc-preview, sends via /adhoc-send.
 *      Perfect for one-off empathy notes / breaking updates that don't
 *      justify a Python deploy.
 *
 * Both modes share the audience picker + confirm step + history strip.
 */
export default function AdminBroadcastCard() {
  const [mode, setMode] = useState("templates"); // "templates" | "adhoc"

  // ---- Templates mode state ----
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [previewAsPro, setPreviewAsPro] = useState(false);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  // ---- Ad-hoc mode state ----
  const [adhocSubject, setAdhocSubject] = useState("");
  const [adhocBody, setAdhocBody] = useState("");
  const [adhocHtml, setAdhocHtml] = useState("");
  const [adhocLoading, setAdhocLoading] = useState(false);
  const adhocDebounceRef = useRef(null);

  // ---- Shared state ----
  const [counts, setCounts] = useState(null);
  const [audience, setAudience] = useState("self_test");
  const [dedupePerRecipient, setDedupePerRecipient] = useState(true);
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [history, setHistory] = useState([]);

  // Initial load: templates + counts + history
  useEffect(() => {
    (async () => {
      try {
        const [t, c, h] = await Promise.all([
          api.get("/admin/broadcasts/templates"),
          api.get("/admin/broadcasts/audience-counts"),
          api.get("/admin/broadcasts/history"),
        ]);
        setTemplates(t.data || []);
        setCounts(c.data || null);
        setHistory(h.data?.broadcasts || []);
        if ((t.data || []).length > 0) setSelectedKey(t.data[0].key);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load broadcasts");
      }
    })();
  }, []);

  // Re-fetch subject preview (cheap — no rendering iframe) whenever
  // template or tier changes.
  useEffect(() => {
    if (!selectedKey) return;
    let cancelled = false;
    setPreviewLoading(true);
    api.post("/admin/broadcasts/preview", {
      template_key: selectedKey,
      as_pro: previewAsPro,
    })
      .then(({ data }) => {
        if (cancelled) return;
        setPreviewSubject(data?.subject || "");
        setPreviewHtml(data?.html || "");
      })
      .catch((e) => {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [selectedKey, previewAsPro]);

  // Fetch per-recipient dedup count whenever template/audience changes.
  // Skipped for self_test (always eligible = 1).
  useEffect(() => {
    if (mode !== "templates" || !selectedKey || audience === "self_test") {
      setRecipientPreview(null);
      return;
    }
    let cancelled = false;
    api.get("/admin/broadcasts/recipient-preview", {
      params: { template_key: selectedKey, audience },
    })
      .then(({ data }) => {
        if (!cancelled) setRecipientPreview(data || null);
      })
      .catch(() => {
        if (!cancelled) setRecipientPreview(null);
      });
    return () => { cancelled = true; };
  }, [mode, selectedKey, audience]);

  // Ad-hoc live preview — debounced so we don't hammer the server as
  // admin types. 450ms feels right: fast enough to feel live,
  // slow enough to not fire on every keystroke.
  useEffect(() => {
    if (mode !== "adhoc") return;
    if (!adhocSubject.trim() && !adhocBody.trim()) {
      setAdhocHtml("");
      return;
    }
    if (adhocDebounceRef.current) clearTimeout(adhocDebounceRef.current);
    let cancelled = false;
    setAdhocLoading(true);
    adhocDebounceRef.current = setTimeout(() => {
      api.post("/admin/broadcasts/adhoc-preview", {
        subject: adhocSubject.trim() || "(no subject yet)",
        markdown_body: adhocBody.trim() || "_(start typing to see the preview…)_",
      })
        .then(({ data }) => { if (!cancelled) setAdhocHtml(data?.html || ""); })
        .catch(() => { /* silent — live preview is best-effort */ })
        .finally(() => { if (!cancelled) setAdhocLoading(false); });
    }, 450);
    return () => { cancelled = true; };
  }, [mode, adhocSubject, adhocBody]);

  const audienceCount = useMemo(() => {
    if (audience === "self_test") return 1;
    return counts?.[audience] ?? 0;
  }, [audience, counts]);

  const effectiveSendCount = useMemo(() => {
    if (audience === "self_test") return 1;
    if (mode === "adhoc") return audienceCount;
    if (!dedupePerRecipient) return audienceCount;
    if (!recipientPreview) return audienceCount;
    return recipientPreview.eligible ?? audienceCount;
  }, [audience, audienceCount, dedupePerRecipient, recipientPreview, mode]);

  const openPreviewWindow = () => {
    const html = mode === "adhoc" ? adhocHtml : previewHtml;
    if (!html) return;
    const w = window.open("", "_blank", "width=720,height=820,scrollbars=yes");
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  const adhocReadyToSend = adhocSubject.trim().length > 0 && adhocBody.trim().length > 0;

  const handleSend = async () => {
    if (mode === "adhoc") {
      if (!adhocReadyToSend) return;
    } else if (!selectedKey) {
      return;
    }
    setSending(true);
    try {
      let res;
      if (mode === "adhoc") {
        res = await api.post("/admin/broadcasts/adhoc-send", {
          subject: adhocSubject.trim(),
          markdown_body: adhocBody,
          audience,
          dedupe_per_recipient: false,
          // Same-day dedup so accidental double-click doesn't blast twice.
          broadcast_key: audience === "self_test"
            ? undefined
            : `adhoc:${new Date().toISOString().slice(0, 10)}:${adhocSubject.trim().slice(0, 40)}:${audience}`,
        });
      } else {
        res = await api.post("/admin/broadcasts/send", {
          template_key: selectedKey,
          audience,
          dedupe_per_recipient: dedupePerRecipient,
          broadcast_key: audience === "self_test"
            ? undefined
            : `${selectedKey}:${audience}:${new Date().toISOString().slice(0, 10)}`,
        });
      }
      const d = res.data;
      if (d.skipped_duplicate) {
        toast.info(`Already sent today (${d.sent} delivered, ${d.failed} failed)`);
      } else {
        const dedupNote = d.skipped_already_received
          ? ` · ${d.skipped_already_received} skipped (already received)`
          : "";
        toast.success(`Sent: ${d.sent} delivered · ${d.failed} failed${dedupNote}`);
      }
      const h = await api.get("/admin/broadcasts/history");
      setHistory(h.data?.broadcasts || []);
      // Refresh recipient preview — dedup count shifts after a send.
      if (mode === "templates" && audience !== "self_test") {
        try {
          const rp = await api.get("/admin/broadcasts/recipient-preview", {
            params: { template_key: selectedKey, audience },
          });
          setRecipientPreview(rp.data || null);
        } catch (_ignored) { /* eligible count refresh failed — UI just shows stale number */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
      setConfirmStep(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-broadcast-card">
      {/* Mode tabs — Templates vs Custom message. */}
      <div className="inline-flex rounded-full border border-border bg-background p-0.5" data-testid="broadcast-mode-toggle">
        <button
          type="button"
          onClick={() => { setMode("templates"); setConfirmStep(false); }}
          className={"h-8 px-3.5 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 " + (mode === "templates" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}
          data-testid="broadcast-mode-templates"
        >
          <Rows weight="duotone" className="h-3.5 w-3.5" />
          Templates
        </button>
        <button
          type="button"
          onClick={() => { setMode("adhoc"); setConfirmStep(false); }}
          className={"h-8 px-3.5 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 " + (mode === "adhoc" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}
          data-testid="broadcast-mode-adhoc"
        >
          <PencilSimpleLine weight="duotone" className="h-3.5 w-3.5" />
          Custom message
        </button>
      </div>

      {/* Composer + audience pickers */}
      <div className="grid gap-3 sm:grid-cols-2">
        {mode === "templates" ? (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              Template
            </label>
            <select
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-background text-sm"
              data-testid="broadcast-template-select"
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            {selectedKey && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {templates.find((t) => t.key === selectedKey)?.description}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold" htmlFor="adhoc-subject">
              Subject line
            </label>
            <input
              id="adhoc-subject"
              type="text"
              value={adhocSubject}
              onChange={(e) => setAdhocSubject(e.target.value)}
              placeholder="e.g. Quick heads-up: parking rules change Monday"
              maxLength={200}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-background text-sm"
              data-testid="broadcast-adhoc-subject"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Shows up as the email subject. {adhocSubject.length}/200
            </p>
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
            Audience
          </label>
          <select
            value={audience}
            onChange={(e) => { setAudience(e.target.value); setConfirmStep(false); }}
            className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-background text-sm"
            data-testid="broadcast-audience-select"
          >
            {AUDIENCE_OPTIONS.map((a) => (
              <option key={a.key} value={a.key}>{a.label}</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-muted-foreground" data-testid="broadcast-audience-count">
            Pool: <strong className="text-foreground">{audienceCount}</strong>{" "}
            {audienceCount === 1 ? "recipient" : "recipients"}.
            {mode === "templates" && dedupePerRecipient && audience !== "self_test" && recipientPreview && (
              <>
                {" "}Will send to <strong className="text-foreground">{effectiveSendCount}</strong>
                {recipientPreview.already_received > 0 && (
                  <> · <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{recipientPreview.already_received} skipped</span> (already received)</>
                )}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Ad-hoc composer body + live preview (only in adhoc mode) */}
      {mode === "adhoc" && (
        <div className="grid gap-3 md:grid-cols-2" data-testid="broadcast-adhoc-composer">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold" htmlFor="adhoc-body">
                Message body <span className="text-muted-foreground/70 normal-case">(markdown supported)</span>
              </label>
              <span className="text-[10.5px] text-muted-foreground">{adhocBody.length}/20000</span>
            </div>
            <textarea
              id="adhoc-body"
              value={adhocBody}
              onChange={(e) => setAdhocBody(e.target.value)}
              placeholder={"Hey {name},\n\nQuick update — **the city changed the food truck permit rules** starting next Monday.\n\n- Point 1\n- Point 2\n\n> Reply to this email if you have questions.\n\nCheck [your dashboard]({app_url}/dashboard) for the latest."}
              maxLength={20000}
              rows={14}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono leading-relaxed resize-y"
              data-testid="broadcast-adhoc-body"
            />
            <p className="mt-1 text-[10.5px] text-muted-foreground leading-snug">
              Tips: <code className="text-[10px] px-1 py-0.5 rounded bg-muted">{"{name}"}</code> becomes the recipient&apos;s first name, <code className="text-[10px] px-1 py-0.5 rounded bg-muted">{"{app_url}"}</code> becomes <code className="text-[10px]">thetrucktrackr.com</code>. Bold with <code className="text-[10px]">**text**</code>, links with <code className="text-[10px]">[label](url)</code>, blockquotes with <code className="text-[10px]">&gt; text</code>.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                <Eye weight="duotone" className="h-3.5 w-3.5" />
                Live preview {adhocLoading && <span className="text-muted-foreground normal-case">· rendering…</span>}
              </label>
              {adhocHtml && (
                <button
                  type="button"
                  onClick={openPreviewWindow}
                  className="text-[10.5px] text-primary hover:underline inline-flex items-center gap-1"
                  data-testid="broadcast-adhoc-preview-open-window"
                >
                  <Eye className="h-3 w-3" />
                  Open in new window
                </button>
              )}
            </div>
            <div
              className="rounded-lg border border-border bg-background overflow-hidden"
              style={{ maxHeight: "420px", overflow: "auto" }}
              data-testid="broadcast-adhoc-preview-frame"
            >
              {adhocHtml ? (
                <iframe
                  title="Ad-hoc email preview"
                  srcDoc={adhocHtml}
                  className="w-full"
                  style={{ minHeight: "420px", border: 0, background: "#f6f6f6" }}
                />
              ) : (
                <div className="p-4 text-[12px] text-muted-foreground italic">
                  Start typing a subject and message body to see the preview here.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subject + preview link (templates mode only) */}
      {mode === "templates" && (
        <div className="rounded-xl border border-border p-3 bg-background/50 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Eye weight="duotone" className="h-3.5 w-3.5" />
              Subject preview
            </h3>
            <div className="inline-flex rounded-full border border-border bg-background p-0.5" data-testid="broadcast-preview-tier-toggle">
              <button
                type="button"
                onClick={() => setPreviewAsPro(false)}
                className={"h-6 px-2.5 rounded-full text-[10.5px] font-medium " + (!previewAsPro ? "bg-foreground text-background" : "text-muted-foreground")}
                data-testid="broadcast-preview-as-free"
              >
                Free
              </button>
              <button
                type="button"
                onClick={() => setPreviewAsPro(true)}
                className={"h-6 px-2.5 rounded-full text-[10.5px] font-medium inline-flex items-center gap-1 " + (previewAsPro ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
                data-testid="broadcast-preview-as-pro"
              >
                <Sparkle weight="fill" className="h-2.5 w-2.5" />
                Pro
              </button>
            </div>
          </div>
          <div
            className="text-sm font-medium break-words"
            data-testid="broadcast-preview-subject"
          >
            {previewLoading ? (
              <span className="text-muted-foreground">Rendering…</span>
            ) : (
              previewSubject || <span className="text-muted-foreground italic">No subject</span>
            )}
          </div>
          {previewHtml && (
            <button
              type="button"
              onClick={openPreviewWindow}
              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
              data-testid="broadcast-preview-open-window"
            >
              <Eye className="h-3 w-3" />
              Open full email preview in a new window
            </button>
          )}
        </div>
      )}

      {/* Per-recipient dedup toggle (templates mode only — ad-hoc is one-off) */}
      {mode === "templates" && audience !== "self_test" && (
        <label
          className="flex items-start gap-2.5 text-[12px] rounded-lg border border-border bg-background/40 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
          data-testid="broadcast-dedupe-toggle"
        >
          <input
            type="checkbox"
            checked={dedupePerRecipient}
            onChange={(e) => setDedupePerRecipient(e.target.checked)}
            className="mt-0.5 accent-primary"
          />
          <span className="flex-1">
            <span className="font-semibold inline-flex items-center gap-1">
              <ShieldCheck weight="duotone" className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              Skip owners who already received this announcement
            </span>
            <span className="block text-muted-foreground text-[11px] mt-0.5">
              Recommended. Filters anyone who received this exact template in a prior broadcast — so re-sending the same template next month only hits new owners.
            </span>
          </span>
        </label>
      )}

      {/* Send actions */}
      <div className="rounded-xl border border-border p-3 bg-background/50">
        {!confirmStep ? (
          <button
            type="button"
            onClick={() => {
              if (audience === "self_test") {
                handleSend();
              } else {
                setConfirmStep(true);
              }
            }}
            disabled={
              sending
              || (mode === "templates" && !selectedKey)
              || (mode === "adhoc" && !adhocReadyToSend)
              || (mode === "templates" && audience !== "self_test" && effectiveSendCount === 0)
            }
            className="w-full h-10 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="broadcast-send-btn"
          >
            <PaperPlaneTilt weight="fill" className="h-4 w-4" />
            {sending ? "Sending…" :
              audience === "self_test" ? "Send test to me"
              : mode === "adhoc" && !adhocReadyToSend ? "Add a subject and body first"
              : effectiveSendCount === 0 ? "All recipients already received this"
              : `Send to ${effectiveSendCount} ${effectiveSendCount === 1 ? "owner" : "owners"}`}
          </button>
        ) : (
          <div className="space-y-3" data-testid="broadcast-confirm-panel">
            <div className="flex items-start gap-2 text-sm">
              <Warning weight="fill" className="h-5 w-5 text-amber-500 flex-none" />
              <div>
                <div className="font-semibold">Confirm broadcast</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  You&apos;re about to email <strong className="text-foreground">{effectiveSendCount} {effectiveSendCount === 1 ? "owner" : "owners"}</strong>.
                  {mode === "templates" && dedupePerRecipient && recipientPreview?.already_received > 0 && (
                    <> {recipientPreview.already_received} more in the pool already received this and will be skipped.</>
                  )}
                  {mode === "adhoc" && (
                    <> This is a one-off ad-hoc message &mdash; no dedup, so every owner in the audience gets it.</>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmStep(false)}
                disabled={sending}
                className="flex-1 h-9 rounded-lg border border-border bg-background font-medium text-sm hover:bg-muted"
                data-testid="broadcast-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="flex-1 h-9 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                data-testid="broadcast-confirm-send-btn"
              >
                {sending ? "Sending…" : "Yes, send now"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <Envelope weight="duotone" className="h-3.5 w-3.5" />
            Recent broadcasts
          </h3>
          <div className="rounded-lg border border-border divide-y divide-border overflow-hidden text-sm" data-testid="broadcast-history">
            {history.slice(0, 6).map((b) => (
              <div key={b.id} className="px-3 py-2 flex items-center gap-3">
                <CheckCircle weight="duotone" className="h-4 w-4 text-emerald-500 flex-none" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate text-[12.5px]">{b.adhoc_subject || b.template_key}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    {b.audience} · {b.sent ?? 0} sent · {b.failed ?? 0} failed
                    {b.skipped_already_received ? ` · ${b.skipped_already_received} skipped` : ""}
                    {b.adhoc_subject ? " · ad-hoc" : ""}
                  </div>
                </div>
                <div className="text-[10.5px] text-muted-foreground flex-none">
                  {b.started_at ? new Date(b.started_at).toLocaleString() : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
